"use client";

import * as Yup from "yup";
import { Field, FormikProvider, useFormik } from "formik";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useState } from "react";
import { defaultCountrySelected } from "@/utils/countryData";
import { contactUsErrorMessage } from "@/utils/error-messages";
import { Input } from "@/components/input/input";
import { TextArea } from "@/components/text-area/text-area";

interface ContactForm {
  fullName: string;
  contactNo: string;
  email: string;
  message: string;
}

const initialValues: ContactForm = {
  fullName: "",
  contactNo: "",
  email: "",
  message: "",
};

const handleSaveUserData = () => {
  //  use this function for further integration: values: ContactForm
};

const phoneUtil = PhoneNumberUtil.getInstance();

const phoneValidation = (phoneNumber: string, countryCode: string) => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode);
    return phoneUtil.isValidNumber(number);
  } catch (err) {
    return false;
  }
};

const ContactForm = () => {
  const [selectedCountryCode] = useState<string>(defaultCountrySelected);

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .matches(/^[A-Za-z\s]+$/, contactUsErrorMessage.fullNameValidate)
      .required(contactUsErrorMessage.fullNameRequired),

    message: Yup.string().required(contactUsErrorMessage.messageRequired),

    email: Yup.string()
      .email(contactUsErrorMessage.emailValidate)
      .required(contactUsErrorMessage.emailRequired),

    contactNo: Yup.string()
      .required(contactUsErrorMessage.phoneNumberRequired)
      .test((value, option) => {
        const { createError, path } = option;
        const validate = phoneValidation(value, selectedCountryCode as string);
        if (!validate) {
          return createError({
            path,
            message: contactUsErrorMessage.phoneNumberInvalid,
          });
        }
        return true;
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: handleSaveUserData,
  });

  const { handleSubmit } = formik;

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
        <div className="flex flex-col lg:flex-row w-full gap-4 sm:gap-5">
          <div className="flex-1 min-w-0">
            <Field name="fullName">
              {({
                field,
                meta,
              }: {
                field: { name: string; value: string; onChange: (e: unknown) => void; onBlur: (e: unknown) => void };
                meta: { error?: string };
              }) => (
                <Input
                  {...field}
                  type="text"
                  label="Full Name"
                  fullWidth
                  required
                  status={meta.error ? "error" : "default"}
                  error={meta.error}
                  containerClassName="w-full"
                />
              )}
            </Field>
          </div>
          <div className="flex-1 min-w-0">
            <Field name="contactNo">
              {({
                field,
                meta,
              }: {
                field: { name: string; value: string; onChange: (e: unknown) => void; onBlur: (e: unknown) => void };
                meta: { error?: string };
              }) => (
                <Input
                  {...field}
                  type="tel"
                  label="Your Phone"
                  fullWidth
                  required
                  status={meta.error ? "error" : "default"}
                  error={meta.error}
                  containerClassName="w-full"
                />
              )}
            </Field>
          </div>
        </div>
        <div className="w-full">
          <Field name="email">
            {({
              field,
              meta,
            }: {
              field: { name: string; value: string; onChange: (e: unknown) => void; onBlur: (e: unknown) => void };
              meta: { error?: string };
            }) => (
              <Input
                {...field}
                type="email"
                label="Your Email"
                fullWidth
                required
                status={meta.error ? "error" : "default"}
                error={meta.error}
                containerClassName="w-full"
              />
            )}
          </Field>
        </div>
        <div className="w-full">
          <Field name="message">
            {({
              field,
              meta,
            }: {
              field: { value: string; onChange: (e: { target: { value: string } }) => void; onBlur: () => void; name: string };
              meta: { error?: string };
            }) => (
              <TextArea
                {...field}
                id="message"
                placeholder="Your Message"
                required
                rows={5}
                error={meta.error}
              />
            )}
          </Field>
        </div>
        <div className="flex w-full lg:justify-end pt-1">
          <button
            type="submit"
            className="transition-all w-full lg:w-auto bg-primary py-3.5 px-10 text-white font-semibold text-base rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98] shadow-sm hover:shadow-md"
          >
            Send Message
          </button>
        </div>
      </form>
    </FormikProvider>
  );
};

export default ContactForm;
